import { useEffect, useState, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { faPen, faFloppyDisk, faTrashCan, faCloudDownloadAlt, faClose, faPlus } from '@fortawesome/pro-light-svg-icons'
import { Button, Input, Select } from '@nazka/nazka.mapframe.components'
import { distance } from '@turf/distance'

import {
  fetchMissionSamplingStrategies, downloadMissionSamplingStrategy, deleteMissionSamplingStrategy,
  createMissionSamplingStrategy, updateMissionSamplingStrategy,
} from '@services/data.service'
import { generateUniqueId } from '@services/utils.service'
import BtnGrid from '@App/components/BtnGrid/BtnGrid'
import IconBtnHor from '@App/components/IconBtnHor/IconBtnHor'
import VisibilityToggles from '@App/components/VisibilityToggles/VisibilityToggles'
import createDeletePopup from '@App/components/Map/popups/DeletePopup/DeletePopup'
import { useAuth } from '@App/components/AuthProvider/AuthProvider'

import './sampling-strategies.styl'

function getStrategyMetadata(strategy) {
  return strategy?.metadata || {}
}

function getStrategyId(strategy) {
  return getStrategyMetadata(strategy).id || strategy?.id
}

function getStrategyName(strategy) {
  return getStrategyMetadata(strategy).name || strategy?.name || ''
}

function getStrategyOwner(strategy) {
  return getStrategyMetadata(strategy).owner || strategy?.owner
}

function getStrategyMission(strategy) {
  return getStrategyMetadata(strategy).mission || strategy?.mission
}

function getStrategyPrivate(strategy) {
  return getStrategyMetadata(strategy).private ?? strategy?.private
}

function getStrategyFeatureCollection(strategy) {
  if (strategy?.type === 'FeatureCollection') return strategy

  return strategy?.sampleLocations || strategy?.traps || {
    type: 'FeatureCollection',
    metadata: {},
    features: [],
  }
}

function withFeatureCustomIds(featureCollection) {
  return {
    ...featureCollection,
    metadata: featureCollection.metadata || {},
    features: (featureCollection.features || []).map(f => ({
      ...f,
      properties: {
        ...f.properties,
        customId: f.properties?.customId || generateUniqueId(),
      },
    })),
  }
}

function buildStrategyMetadata(strategy, owner) {
  return {
    ...getStrategyMetadata(strategy),
    id: getStrategyId(strategy),
    name: getStrategyName(strategy),
    private: getStrategyPrivate(strategy),
    owner,
    mission: getStrategyMission(strategy),
  }
}

function normalizeEditableStrategy(strategy, owner) {
  const featureCollection = withFeatureCustomIds(getStrategyFeatureCollection(strategy))

  return {
    ...featureCollection,
    metadata: buildStrategyMetadata({ ...strategy, ...featureCollection }, owner),
  }
}

const SamplingStrategies = ({ map }) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const activeProject = useSelector(state => state.missionSetup.activeProject)
  const activeExtraLayers = useSelector(state => state.missionSetup.activeExtraLayers ?? [])
  const [activeStrategyId, setActiveStrategyId] = useState(null)
  const [editingStrategy, setEditingStrategy] = useState(null)
  const [downloadInProgress, setDownloadInProgress] = useState(false)
  const [updateInProgress, setUpdateInProgress] = useState(false)
  const [visibility, setVisibility] = useState('private')
  const [createMenuOpen, setCreateMenuOpen] = useState(false)
  const [uploadError, setUploadError] = useState(null)

  const popupRef = useRef(null)
  const createMenuRef = useRef(null)
  const uploadInputRef = useRef(null)

  const { data: samplings } = useQuery({
    queryKey: ['samplings', activeProject?.id],
    queryFn: () => fetchMissionSamplingStrategies(activeProject?.id),
  })

  const options = useMemo(() => {
    return samplings?.map(sampling => ({
      label: getStrategyName(sampling),
      id: getStrategyId(sampling),
      value: sampling,
    })) ?? []
  }, [samplings])

  const edit = editingStrategy !== null
  const activeStrategy = useMemo(() => (
    edit ? editingStrategy : samplings?.find(s => getStrategyId(s) === activeStrategyId)
  ), [edit, editingStrategy, samplings, activeStrategyId])

  const buildNewEditableStrategy = (name = '', features = []) => ({
    type: 'FeatureCollection',
    metadata: {
      id: 'new',
      name,
      private: visibility === 'private',
      owner: user?.sub,
      mission: activeProject.id,
    },
    features,
  })

  const startNewStrategy = () => {
    setUploadError(null)
    setActiveStrategyId('new')
    setEditingStrategy(buildNewEditableStrategy())
  }

  const handleGeojsonUpload = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      const parsedGeojson = JSON.parse(await file.text())

      if (parsedGeojson?.type !== 'FeatureCollection' || !Array.isArray(parsedGeojson.features)) {
        throw new Error('Uploaded file is not a GeoJSON FeatureCollection')
      }

      const featureCollection = withFeatureCustomIds(parsedGeojson)
      const fileName = file.name.replace(/\.(geo)?json$/i, '')
      const strategyName = getStrategyName(featureCollection) || fileName

      setUploadError(null)
      setActiveStrategyId('new')
      setEditingStrategy({
        ...featureCollection,
        metadata: {
          ...featureCollection.metadata,
          id: 'new',
          name: strategyName,
          private: visibility === 'private',
          owner: user?.sub,
          mission: activeProject.id,
        },
      })
    } catch (error) {
      console.error('Failed to upload sampling strategy GeoJSON:', error)
      setUploadError('Upload a valid GeoJSON FeatureCollection.')
    } finally {
      event.target.value = ''
    }
  }

  useEffect(() => {
    if (!createMenuOpen) return undefined

    const handleOutsideClick = (event) => {
      if (!createMenuRef.current || createMenuRef.current.contains(event.target)) return
      setCreateMenuOpen(false)
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('touchstart', handleOutsideClick)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('touchstart', handleOutsideClick)
    }
  }, [createMenuOpen])

  useEffect(() => {
    const featureCollection = getStrategyFeatureCollection(activeStrategy)

    if (featureCollection.features.length && map?.getSource('planner-traps')) {
      map.getSource('planner-traps').setData(withFeatureCustomIds(featureCollection))
    }
    return () => {
      if (map?.getSource('planner-traps')) {
        map.getSource('planner-traps').setData({
          type: 'FeatureCollection',
          features: [],
        })
      }
    }
  }, [map, samplings, activeStrategy])

  useEffect(() => {
    const canvasContainer = document.querySelector('.maplibregl-canvas')
    if (edit && canvasContainer) canvasContainer.classList.add('crosshair-cursor')
    else if (canvasContainer) canvasContainer.classList.remove('crosshair-cursor')

    return () => {
      if (canvasContainer) canvasContainer.classList.remove('crosshair-cursor')
    }
  }, [edit])

  useEffect(() => {
    if (!edit || !map) return

    const canvasContainer = document.querySelector('.maplibregl-canvas')
    if (!canvasContainer) return

    const handleMapClick = (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['planner-traps-layer'],
      })

      if (features.length) {
        const feature = features[0]
        const clickedId = feature.properties?.customId

        if (popupRef.current) {
          popupRef.current.remove()
          popupRef.current = null
        }

        popupRef.current = createDeletePopup({
          map,
          coordinates: feature.geometry.coordinates,
          onDelete: () => {
            setEditingStrategy(prev => ({
              ...prev,
              features: prev.features.filter((f) => {
                return f.properties?.customId !== clickedId
              }),
            }))
            popupRef.current = null
          },
        })
      } else {
        if (popupRef.current) {
          popupRef.current.remove()
          popupRef.current = null
        }

        const newFeature = {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [e.lngLat.lng, e.lngLat.lat],
          },
          properties: {
            customId: generateUniqueId(),
          },
        }
        setEditingStrategy(prev => ({
          ...prev,
          features: [...(prev.features || []), newFeature],
        }))
      }
    }

    const handleMouseEnter = () => {
      canvasContainer.classList.add('pointer-cursor')
    }

    const handleMouseLeave = () => {
      canvasContainer.classList.remove('pointer-cursor')
    }

    const handleMouseMove = (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['planner-traps-layer'],
      })

      if (features.length > 0) {
        const hoveredFeature = features[0]
        const hoveredCoords = hoveredFeature.geometry.coordinates

        const plannerTrapsFeatures = map.querySourceFeatures('planner-traps')
        const trapsFeatures = map.querySourceFeatures('traps')
        const allFeatures = [...plannerTrapsFeatures, ...trapsFeatures]

        const lines = allFeatures
          .filter((f) => {
            const coords = f.geometry.coordinates
            return coords[0] !== hoveredCoords[0] || coords[1] !== hoveredCoords[1]
          })
          .map((feature) => {
            const targetCoords = feature.geometry.coordinates
            const lineLength = distance(hoveredCoords, targetCoords, { units: 'meters' }).toFixed(2)

            return {
              type: 'Feature',
              geometry: {
                type: 'LineString',
                coordinates: [hoveredCoords, targetCoords],
              },
              properties: {
                length: `${lineLength} m`,
              },
            }
          })

        if (map.getSource('trap-connections')) {
          map.getSource('trap-connections').setData({
            type: 'FeatureCollection',
            features: lines,
          })
        }
        return
      }

      const cursorCoords = [e.lngLat.lng, e.lngLat.lat]

      const plannerTrapsFeatures = map.querySourceFeatures('planner-traps')
      const trapsFeatures = map.querySourceFeatures('traps')
      const allFeatures = [...plannerTrapsFeatures, ...trapsFeatures]

      if (allFeatures.length === 0) {
        if (map.getSource('trap-connections')) {
          map.getSource('trap-connections').setData({
            type: 'FeatureCollection',
            features: [],
          })
        }
        return
      }

      // Find the closest trap
      let closestFeature = null
      let minDistance = Infinity

      allFeatures.forEach((feature) => {
        const targetCoords = feature.geometry.coordinates
        const dist = distance(cursorCoords, targetCoords, { units: 'meters' })
        if (dist < minDistance) {
          minDistance = dist
          closestFeature = feature
        }
      })

      // Create line only to the closest trap
      const lines = closestFeature
        ? [{
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [cursorCoords, closestFeature.geometry.coordinates],
            },
            properties: {
              length: `${minDistance.toFixed(2)} m`,
            },
          }]
        : []

      if (map.getSource('trap-connections')) {
        map.getSource('trap-connections').setData({
          type: 'FeatureCollection',
          features: lines,
        })
      }
    }

    const handleCanvasMouseLeave = () => {
      if (map.getSource('trap-connections')) {
        map.getSource('trap-connections').setData({
          type: 'FeatureCollection',
          features: [],
        })
      }
    }

    map.on('click', handleMapClick)
    map.on('mousemove', handleMouseMove)
    map.on('mouseenter', 'planner-traps-layer', handleMouseEnter)
    map.on('mouseleave', 'planner-traps-layer', handleMouseLeave)
    canvasContainer.addEventListener('mouseleave', handleCanvasMouseLeave)

    return () => {
      map.off('click', handleMapClick)
      map.off('mousemove', handleMouseMove)
      map.off('mouseenter', 'planner-traps-layer', handleMouseEnter)
      map.off('mouseleave', 'planner-traps-layer', handleMouseLeave)
      canvasContainer.removeEventListener('mouseleave', handleCanvasMouseLeave)
      canvasContainer.classList.remove('pointer-cursor')
      if (popupRef.current) {
        popupRef.current.remove()
        popupRef.current = null
      }
      if (map.getSource('trap-connections')) {
        map.getSource('trap-connections').setData({
          type: 'FeatureCollection',
          features: [],
        })
      }
    }
  }, [edit, map])

  useEffect(() => {
    if (!map || edit || !activeExtraLayers.includes('distance')) return

    const handleHoverEnter = (e) => {
      if (e.features.length === 0) return

      const hoveredFeature = e.features[0]

      const hoveredCoords = hoveredFeature.geometry.coordinates

      // Get all features from both planner-traps and traps sources
      const plannerTrapsFeatures = map.querySourceFeatures('planner-traps')
      const trapsFeatures = map.querySourceFeatures('traps')
      const allFeatures = [...plannerTrapsFeatures, ...trapsFeatures]

      // Create lines to all other features
      const lines = allFeatures
        .filter((f) => {
          const coords = f.geometry.coordinates
          return coords[0] !== hoveredCoords[0] || coords[1] !== hoveredCoords[1]
        })
        .map((feature) => {
          const targetCoords = feature.geometry.coordinates
          const lineLength = distance(hoveredCoords, targetCoords, { units: 'meters' }).toFixed(2)

          return {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [hoveredCoords, targetCoords],
            },
            properties: {
              length: `${lineLength} m`,
            },
          }
        })

      if (map.getSource('trap-connections')) {
        map.getSource('trap-connections').setData({
          type: 'FeatureCollection',
          features: lines,
        })
      }
    }

    const handleHoverLeave = () => {
      if (map.getSource('trap-connections')) {
        map.getSource('trap-connections').setData({
          type: 'FeatureCollection',
          features: [],
        })
      }
    }

    map.on('mouseenter', 'planner-traps-layer', handleHoverEnter)
    map.on('mouseleave', 'planner-traps-layer', handleHoverLeave)
    map.on('mouseenter', 'trap-layer', handleHoverEnter)
    map.on('mouseleave', 'trap-layer', handleHoverLeave)

    return () => {
      map.off('mouseenter', 'planner-traps-layer', handleHoverEnter)
      map.off('mouseleave', 'planner-traps-layer', handleHoverLeave)
      map.off('mouseenter', 'trap-layer', handleHoverEnter)
      map.off('mouseleave', 'trap-layer', handleHoverLeave)
      if (map.getSource('trap-connections')) {
        map.getSource('trap-connections').setData({
          type: 'FeatureCollection',
          features: [],
        })
      }
    }
  }, [map, edit, activeExtraLayers])

  return (
    <div className={`sampling-strategies${updateInProgress ? ' is-loading' : ''}`}>
      <div className="sampling-strategies__select-wrapper">
        {!edit
          ? (
              <Select
                className="sampling-strategies__select-wrapper__select"
                value={activeStrategyId}
                onInputChange={o => setActiveStrategyId(o?.id ?? null)}
                options={options}
                placeholder="Select strategy..."
              />
            )
          : (
              <Input
                className="sampling-strategies__select-wrapper__input"
                value={getStrategyName(editingStrategy)}
                onInputChange={e => setEditingStrategy({
                  ...editingStrategy,
                  metadata: {
                    ...editingStrategy.metadata,
                    name: e.target.value,
                  },
                })}
                placeholder="New sampling strategy name..."
              />
            )}
        {edit && getStrategyId(activeStrategy) && getStrategyId(activeStrategy) !== 'new' && getStrategyOwner(activeStrategy) === user?.sub && (
          <Button
            className="sampling-strategies__select-wrapper__button"
            iconProps={{ icon: faTrashCan, size: '18px' }}
            rootElProps={{ title: 'Delete sampling strategy' }}
            disabled={updateInProgress}
            onClick={async () => {
              setUpdateInProgress(true)
              try {
                await deleteMissionSamplingStrategy(getStrategyId(activeStrategy))
                await queryClient.invalidateQueries({ queryKey: ['samplings', activeProject?.id] })
                setActiveStrategyId(null)
                setEditingStrategy(null)
              } catch (error) {
                console.error('Failed to delete sampling strategy:', error)
              } finally {
                setUpdateInProgress(false)
              }
            }}
          />
        )}
        {!edit && (
          <div className="sampling-strategies__create-menu" ref={createMenuRef}>
            <Button
              className="sampling-strategies__select-wrapper__button"
              iconProps={{ icon: faPlus, size: '20px' }}
              rootElProps={{ title: 'Create sampling strategy' }}
              onClick={() => setCreateMenuOpen(prev => !prev)}
            />
            {createMenuOpen && (
              <div className="sampling-strategies__create-menu__list z-depth-1">
                <button
                  type="button"
                  className="sampling-strategies__create-menu__item"
                  onClick={() => {
                    setCreateMenuOpen(false)
                    startNewStrategy()
                  }}
                >
                  New strategy
                </button>
                <button
                  type="button"
                  className="sampling-strategies__create-menu__item"
                  onClick={() => {
                    setCreateMenuOpen(false)
                    uploadInputRef.current?.click()
                  }}
                >
                  Upload GeoJSON
                </button>
              </div>
            )}
            <input
              ref={uploadInputRef}
              className="sampling-strategies__create-menu__input"
              type="file"
              accept=".geojson,.json,application/geo+json,application/json"
              onChange={handleGeojsonUpload}
            />
          </div>
        )}
      </div>
      {uploadError && (
        <p className="sampling-strategies__error">
          {uploadError}
        </p>
      )}
      {edit && (
        <>
          <VisibilityToggles
            value={visibility}
            onChange={(value) => {
              setVisibility(value)
              setEditingStrategy(prev => ({
                ...prev,
                metadata: {
                  ...prev.metadata,
                  private: value === 'private',
                },
              }))
            }}
          />
          <p className="sampling-strategies__text">
            Click on the map to add or remove a trap location.
          </p>
        </>
      )}
      <BtnGrid>
        {!edit && (
          <IconBtnHor
            iconProps={{ icon: faPen, size: '20px' }}
            onClick={() => {
              setEditingStrategy(normalizeEditableStrategy(activeStrategy, user?.sub))
            }}
            disabled={!activeStrategy || getStrategyOwner(activeStrategy) !== user?.sub}
          >
            Edit
          </IconBtnHor>
        )}
        {!edit && (
          <IconBtnHor
            isLoading={downloadInProgress}
            iconProps={{ icon: faCloudDownloadAlt, size: '24px' }}
            onClick={async () => {
              if (getStrategyId(activeStrategy)) {
                setDownloadInProgress(true)
                const response = await downloadMissionSamplingStrategy(getStrategyId(activeStrategy))
                const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `sampling-strategy-${getStrategyName(activeStrategy) || getStrategyId(activeStrategy)}.geojson`
                a.click()
                URL.revokeObjectURL(url)
                setDownloadInProgress(false)
              }
            }}
            disabled={!activeStrategy}
          >
            Download
          </IconBtnHor>
        )}
        {edit && (
          <IconBtnHor
            iconProps={{ icon: faFloppyDisk, size: '20px' }}
            onClick={async () => {
              const strategyId = getStrategyId(editingStrategy)
              const samplingStrategy = {
                type: 'FeatureCollection',
                metadata: buildStrategyMetadata(editingStrategy, user?.sub),
                features: editingStrategy?.features || [],
              }
              setUpdateInProgress(true)

              if (strategyId === 'new') {
                const { data } = await createMissionSamplingStrategy(samplingStrategy)
                await queryClient.invalidateQueries({ queryKey: ['samplings', activeProject?.id] })
                setActiveStrategyId(data?.metadata?.id ?? null)
              } else {
                await updateMissionSamplingStrategy(strategyId, samplingStrategy)
                await queryClient.invalidateQueries({ queryKey: ['samplings', activeProject?.id] })
              }
              setEditingStrategy(null)
              setUpdateInProgress(false)
            }}
            disabled={!activeStrategy || getStrategyName(editingStrategy).trim() === '' || editingStrategy?.features?.length === 0}
          >
            Save
          </IconBtnHor>
        )}
        {edit && (
          <IconBtnHor
            iconProps={{ icon: faClose, size: '22px' }}
            onClick={() => {
              setEditingStrategy(null)
              if (activeStrategyId === 'new') setActiveStrategyId(null)
            }}
            disabled={!activeStrategy}
          >
            Cancel
          </IconBtnHor>
        )}
      </BtnGrid>
    </div>
  )
}

export default SamplingStrategies
